import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { localserver } from "../../../api/common";
import moment from "moment";
import AddUpdateEvent from "./AddUpdateEvent";
import { showToast, ToastMessage } from "../../Shared/ToastMessage";
import { ROUTE_BASE_URL } from "../../../api/common";
import { useUser } from "../../AdminUsers/UserContext";
import { FaChevronRight, FaCalendar } from "react-icons/fa"

const EventManagement = () => {
  const { checkPermission } = useUser();
  const [dropdownStates, setDropdownStates] = useState({});
  const dropdownRefs = useRef({});
  const [isEventSidebarOpen, setEventSidebarOpen] = useState(false);
  const [isModal, setModal] = useState(false);
  const [eventStatus, setEventStatus] = useState("");
  const [eventData, setEventData] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [totalEvents, setTotalEvents] = useState([]);
  const [eventCounts, setEventCounts] = useState({});
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [activeTab, setActiveTab] = useState("published");
  const [searchTerm, setSearchTerm] = useState("");

  const isCurrentlyLive = useCallback((event) => {
    if (!event || !event.start_date || !event.end_date || !event.start_time || !event.end_time) {
      return false;
    }
    const now = moment();
    
    // Construct start datetime using start_date (date part) and start_time
    const startDate = moment(event.start_date).local().format('YYYY-MM-DD');
    const startDatetime = moment(`${startDate} ${event.start_time}`, 'YYYY-MM-DD HH:mm:ss');
    
    // Construct end datetime using end_date (date part) and end_time
    const endDate = moment(event.end_date).local().format('YYYY-MM-DD');
    const endDatetime = moment(`${endDate} ${event.end_time}`, 'YYYY-MM-DD HH:mm:ss');

    const isLive = now.isSameOrAfter(startDatetime) && now.isSameOrBefore(endDatetime);

    // Debug log for troubleshooting
    console.log("Live check for event:", event.event_name, {
      now: now.format(),
      start: startDatetime.format(),
      end: endDatetime.format(),
      isLive,
    });

    return isLive;
  }, []);

  const handleEventAction = (index) => {
    setDropdownStates((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleCreateEvent = async () => {
    const hasPermission = await checkPermission("Create an event", "create");
    if (!hasPermission) return;
    setEventSidebarOpen(true);
  };

  const handleStatusChange = (status, event) => {
    if (event.event_status === status) {
      showToast(`Event is already ${status}`, "info");
      return;
    }
    setEventStatus(status);
    setSelectedEvent(event);
    setModal(true);
  };

  const handleCancelStatusChange = () => {
    setEventStatus("");
    setSelectedEvent(null);
    setModal(false);
  };

  const handleConfirmStatusChange = () => {
    if (selectedEvent && eventStatus) {
      updateEventStatusByUser(selectedEvent, eventStatus);
      handleCancelStatusChange();
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      let isClickInside = false;
      Object.values(dropdownRefs.current).forEach((ref) => {
        if (ref && ref.contains(event.target)) {
          isClickInside = true;
        }
      });
      if (!isClickInside) {
        setDropdownStates({});
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const formatDate = (date) => moment(date).format("DD MMM YYYY");

  const fetchCounts = async () => {
    try {
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("access_token")}`,
      };

      // Fetch all for basic counts
      const allResponse = await axios.post(
        `${localserver}/get_events_by_user`,
        { event_status: "all" },
        { headers }
      );

      let allCount = 0;
      let draftCount = 0;
      let archivedCount = 0;
      let deletedCount = 0;

      if (allResponse.data.success) {
        const allEventsData = allResponse.data.events;
        setTotalEvents(allEventsData);
        allCount = allEventsData.length;
        draftCount = allEventsData.filter((e) => e.event_status === "draft").length;
        archivedCount = allEventsData.filter((e) => e.event_status === "archived").length;
        deletedCount = allEventsData.filter((e) => e.event_status === "deleted").length;
      }

      // Fetch published for non-live published count
      const pubResponse = await axios.post(
        `${localserver}/get_events_by_user`,
        { event_status: "published",
          community:1
         },
        { headers }
      );

      let publishedCount = 0;
      if (pubResponse.data.success) {
        publishedCount = pubResponse.data.events.length;
      }

      // Fetch live for live count
      const liveResponse = await axios.post(
        `${localserver}/get_events_by_user`,
        { event_status: "live",
          community:1
         },
        { headers }
      );

      let liveCount = 0;
      if (liveResponse.data.success) {
        liveCount = liveResponse.data.events.length;
      }

      const counts = {
        all: allCount,
        live: liveCount,
        published: publishedCount,
        draft: draftCount,
        archived: archivedCount,
        deleted: deletedCount,
      };
      setEventCounts(counts);
    } catch (error) {
      console.error("Error fetching counts:", error);
      showToast("An error occurred while fetching events", "error");
    }
  };

  const getTabEvents = async (event_status) => {
    try {
      const response = await axios.post(
        `${localserver}/get_events_by_user`,
        { event_status,
          community:1
         },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );
      if (response.data.success) {
        const events = response.data.events;
        setEventData(events);
        setAllEvents(events);
      } else {
        console.error("Error: Failed to fetch user data");
        showToast("Failed to fetch events", "error");
      }
    } catch (error) {
      console.error("Error fetching events:", error);
      showToast("An error occurred while fetching events", "error");
    }
  };

  const handleEventSearch = (term) => {
    setSearchTerm(term);
    const searchTerm = term.toLowerCase();
    if (searchTerm === "") {
      setEventData(allEvents);
      return;
    }
    const filteredEvents = allEvents.filter(
      (event) =>
        event.event_name.toLowerCase().includes(searchTerm) ||
        event.event_type.toLowerCase().includes(searchTerm)
    );
    setEventData(filteredEvents);
  };

  const updateEventStatusByUser = async (eventDetails, newStatus) => {
    const eventData = {
      event_id: eventDetails.id,
      event_status: newStatus,
    };

    try {
      const response = await axios.post(
        `${localserver}/updateEventstatus`,
        eventData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );
      if (response.data.success) {
        showToast(`Event ${newStatus} successfully`, "success");
        await fetchCounts();
        await getTabEvents(activeTab);
      } else {
        throw new Error(
          response.data.message || "Failed to update event status"
        );
      }
    } catch (error) {
      console.error("Error updating event status:", error);
      showToast(
        error.message || "An error occurred while updating the event",
        "error"
      );
    }
  };

  useEffect(() => {
    fetchCounts();
  }, []);

  useEffect(() => {
    getTabEvents(activeTab);
  }, [activeTab]);

  // JSX Methods for UI Components
  const renderHeader = () => {
    return (
      <div style={{ marginBottom: "22px" }}>
        <div
          className="brood_comes_div"
        >
          <div className="row align-items-center">
            <div className="col-md-6 brood_comes_heading">
              <h2>
                <div className="brood_comes_part">
                  <FaCalendar size={20} />
                </div>
                All Events
              </h2>
            </div>

            <div className="col-md-6 text-end">
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
                <div style={{ position: "relative" }}>
                  <i
                    className="fa fa-search"
                    style={{
                      position: "absolute",
                      left: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#666666",
                      fontSize: "14px",
                    }}
                  ></i>
                  <input
                    type="text"
                    placeholder="Search events..."
                    value={searchTerm}
                    onChange={(e) => handleEventSearch(e.target.value)}
                    style={{
                      padding: "10px 16px 10px 36px",
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      fontSize: "14px",
                      outline: "none",
                      width: "250px",
                      transition: "border-color 0.2s ease-in-out",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "#184778")}
                    onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
                  />
                </div>
                <button
                  onClick={() => handleCreateEvent()}
                  style={{
                    background: "#184778",
                    color: "#ffffff",
                    border: "none",
                    borderRadius: "8px",
                    padding: "10px 20px",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    transition: "all 0.3s ease",
                    boxShadow: "0 2px 4px rgba(24, 71, 120, 0.2)",
                    opacity: 1,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#0f3a5f"
                    e.currentTarget.style.transform = "translateY(-2px)"
                    e.currentTarget.style.boxShadow = "0 4px 8px rgba(24, 71, 120, 0.3)"
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "#184778"
                    e.currentTarget.style.transform = "translateY(0)"
                    e.currentTarget.style.boxShadow = "0 2px 4px rgba(24, 71, 120, 0.2)"
                  }}
                >
                  <i className="fa fa-plus"></i>
                  Create Event
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTabButton = (status, label, count) => {
    const isActive = activeTab === status;

    return (
      <button
        key={status}
        onClick={() => {
          setActiveTab(status);
        }}
        style={{
          padding: "12px 20px",
          border: "none",
          backgroundColor: isActive ? "#184778" : "transparent",
          color: isActive ? "#ffffff" : "#666666",
          fontSize: "14px",
          fontWeight: "500",
          cursor: "pointer",
          borderRadius: "8px",
          transition: "all 0.2s ease-in-out",
          marginRight: "8px",
          marginBottom: "8px",
        }}
        onMouseOver={(e) => {
          if (!isActive) {
            e.target.style.backgroundColor = "#f3f4f6";
          }
        }}
        onMouseOut={(e) => {
          if (!isActive) {
            e.target.style.backgroundColor = "transparent";
          }
        }}
      >
        {label} ({count || 0})
      </button>
    );
  };

  const renderTabs = () => {
    const tabs = [
      { key: "published", label: "Published" },      
      { key: "live", label: "Live" },
      { key: "all", label: "All" },
      { key: "draft", label: "Draft" },
      { key: "archived", label: "Archived" },
      { key: "deleted", label: "Deleted" },
    ];

    return (
      <div
        style={{
          backgroundColor: "#ffffff",
          padding: "20px 32px",
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          marginBottom: "24px",
        }}
      >
        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
          {tabs.map((tab) =>
            renderTabButton(tab.key, tab.label, eventCounts[tab.key])
          )}
        </div>
      </div>
    );
  };

  const renderEventCard = (event, index) => {
    const statusColors = {
      live: "#10b981",
      published: "#3b82f6",
      draft: "#f59e0b",
      archived: "#6b7280",
      deleted: "#ef4444",
    };

    return (
      <Link
        key={event.id}
        to={`${ROUTE_BASE_URL}/event-overview/${event.id}`}
        onClick={() => {
          setTimeout(() => {
            window.location.reload();
          }, 500);
        }}
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          overflow: "hidden",
          transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
          position: "relative",
          marginBottom: "24px",
          textDecoration: "none",
          color: "inherit",
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.boxShadow = "0 4px 16px rgba(0, 0, 0, 0.15)";
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.1)";
        }}
      >
        {/* Banner Image */}
        <div
          style={{
            height: "160px",
            background: event.event_banner
              ? `linear-gradient(135deg, rgba(66, 133, 244, 0.8), rgba(66, 133, 244, 0.6)), url(${event.event_banner})`
              : "linear-gradient(135deg, #184778, #5380c999)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Overlay pattern for better visual appeal */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                "linear-gradient(45deg, rgba(255,255,255,0.1) 25%, transparent 25%, transparent 75%, rgba(255,255,255,0.1) 75%), linear-gradient(45deg, rgba(255,255,255,0.1) 25%, transparent 25%, transparent 75%, rgba(255,255,255,0.1) 75%)",
              backgroundSize: "20px 20px",
              backgroundPosition: "0 0, 10px 10px",
              opacity: 0.3,
            }}
          ></div>

          {/* Event Logo positioned over banner */}
            <div
              style={{
                position: "relative",
                zIndex: 2,
              }}
            >
              {event.event_logo ? (
                <img
                  src={event.event_logo || "/placeholder.svg"}
                  alt="Event Logo"
                  style={{
                    width: "80px",
                    height: "80px",
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "4px solid #ffffff",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
                  }}
                />
              ) : (
                <div
                  style={{
                    width: "80px",
                    height: "80px",
                    borderRadius: "50%",
                    backgroundColor: "#ffffff",
                    border: "4px solid #ffffff",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "32px",
                    color: "#184778",
                    fontWeight: "bold",
                  }}
                >
                  <i className="fa fa-user"></i>
                </div>
              )}
            </div>

          {/* Status Badge positioned on banner */}
          <div
            style={{
              position: "absolute",
              top: "12px",
              right: "12px",
              zIndex: 3,
            }}
          >
            <span
              style={{
                padding: "4px 12px",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: "500",
                backgroundColor: statusColors[event.event_status] || "#6b7280",
                color: "#ffffff",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
              }}
            >
              {event.event_status.charAt(0).toUpperCase() +
                event.event_status.slice(1)}
            </span>
          </div>

          {/* Dropdown Menu positioned on banner */}
          <div
            style={{
              position: "absolute",
              top: "12px",
              left: "12px",
              zIndex: 3,
            }}
          >
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleEventAction(index);
              }}
              style={{
                background: "rgba(255, 255, 255, 0.9)",
                border: "none",
                cursor: "pointer",
                padding: "8px",
                borderRadius: "50%",
                color: "#666666",
                fontSize: "16px",
                width: "36px",
                height: "36px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                transition: "all 0.2s ease-in-out",
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = "#ffffff";
                e.target.style.transform = "scale(1.1)";
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = "rgba(255, 255, 255, 0.9)";
                e.target.style.transform = "scale(1)";
              }}
            >
              <i className="fa fa-ellipsis-v"></i>
            </button>

            {dropdownStates[index] && (
              <div
                ref={(el) => (dropdownRefs.current[index] = el)}
                style={{
                  position: "absolute",
                  top: "100%",
                  left: "0",
                  backgroundColor: "#ffffff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  boxShadow: "0 4px 16px rgba(0, 0, 0, 0.1)",
                  zIndex: 1000,
                  minWidth: "120px",
                  marginTop: "4px",
                }}
              >
                {event.event_status !== "archived" && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleStatusChange("archived", event);
                    }}
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      border: "none",
                      background: "none",
                      textAlign: "left",
                      cursor: "pointer",
                      fontSize: "14px",
                      color: "#374151",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      borderRadius: "10px",
                    }}
                    onMouseOver={(e) =>
                      (e.target.style.backgroundColor = "#f3f4f6")
                    }
                    onMouseOut={(e) =>
                      (e.target.style.backgroundColor = "transparent")
                    }
                  >
                    <i className="fa fa-archive"></i>
                    Archive
                  </button>
                )}
                {event.event_status !== "deleted" && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleStatusChange("deleted", event);
                    }}
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      border: "none",
                      background: "none",
                      textAlign: "left",
                      cursor: "pointer",
                      fontSize: "14px",
                      color: "#ef4444",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      borderRadius: "10px",
                    }}
                    onMouseOver={(e) =>
                      (e.target.style.backgroundColor = "#fef2f2")
                    }
                    onMouseOut={(e) =>
                      (e.target.style.backgroundColor = "transparent")
                    }
                  >
                    <i className="fa fa-trash"></i>
                    Delete
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Card Content */}
        <div style={{ padding: "24px" }}>
          {/* Event Title */}
          <div
            style={{
              color: "#1a1a1a",
              textDecoration: "none",
              fontSize: "18px",
              fontWeight: "600",
              display: "block",
              lineHeight: "1.4",
            }}
            onMouseOver={(e) => (e.target.style.color = "#0e70daff")}
            onMouseOut={(e) => (e.target.style.color = "#1a1a1a")}
          >
            <div style={{ textAlign: "center", marginBottom: "16px" }}>
              {event.event_name}
            </div>
          </div>

          {/* Event Details */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-around",
              padding: "16px 0",
              borderTop: "1px solid #f3f4f6",
              borderBottom: "1px solid #f3f4f6",
            }}
          >
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: "16px",
                  color: "#184778",
                  marginBottom: "4px",
                }}
              >
                <i className="fa fa-calendar"></i>
              </div>
              <div style={{ fontSize: "12px", color: "#666666" }}>
                {event.event_date ? formatDate(event.event_date) : "--"}
              </div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: "16px",
                  color: "#184778",
                  marginBottom: "4px",
                }}
              >
                <i className="fa fa-building-o"></i>
              </div>
              <div style={{ fontSize: "12px", color: "#666666" }}>
                {event.event_type || "Onsite"}
              </div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: "16px",
                  color: "#184778",
                  marginBottom: "4px",
                }}
              >
                <i className="fa fa-ticket"></i>
              </div>
              <div style={{ fontSize: "12px", color: "#666666" }}>
                {event.total_ticket_quantity == 0
                  ? "∞"
                  : event.total_ticket_quantity}
              </div>
            </div>
          </div>

          {/* Event Description (if available) */}
          {event.description && (
            <div
              style={{
                marginTop: "16px",
                fontSize: "14px",
                color: "#666666",
                lineHeight: "1.5",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {event.description}
            </div>
          )}
        </div>
      </Link>
    );
  };

  const renderEventGrid = () => {
    return (
      <div
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          padding: "32px",
        }}
      >
        {eventData.length > 0 ? (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
              gap: "24px",
            }}
          >
            {eventData.map((event, index) => renderEventCard(event, index))}
          </div>
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "60px 20px",
              color: "#666666",
            }}
          >
            <div style={{ fontSize: "48px", marginBottom: "16px" }}>
              <i className="fa fa-calendar-o"></i>
            </div>
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "600",
                marginBottom: "8px",
                color: "#1a1a1a",
              }}
            >
              No events found
            </h3>
            <p style={{ fontSize: "14px", margin: "0" }}>
              {searchTerm
                ? "Try adjusting your search terms"
                : "Create your first event to get started"}
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderConfirmationModal = () => {
    if (!isModal) return null;

    return (
      <>
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={handleCancelStatusChange}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "12px",
              padding: "32px",
              maxWidth: "400px",
              width: "90%",
              textAlign: "center",
              position: "relative",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleCancelStatusChange}
              style={{
                position: "absolute",
                top: "16px",
                right: "16px",
                background: "none",
                border: "none",
                fontSize: "20px",
                cursor: "pointer",
                color: "#666666",
              }}
            >
              <i className="fa fa-times"></i>
            </button>

            <div
              style={{
                width: "64px",
                height: "64px",
                borderRadius: "50%",
                backgroundColor:
                  eventStatus === "deleted" ? "#fef2f2" : "#f0f9ff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 24px auto",
              }}
            >
              <i
                className={
                  eventStatus === "deleted" ? "fa fa-trash" : "fa fa-archive"
                }
                style={{
                  fontSize: "24px",
                  color: eventStatus === "deleted" ? "#ef4444" : "#184778",
                }}
              ></i>
            </div>

            <h3
              style={{
                fontSize: "20px",
                fontWeight: "600",
                color: "#1a1a1a",
                marginBottom: "12px",
                margin: "0 0 12px 0",
              }}
            >
              {eventStatus === "deleted"
                ? "Delete"
                : eventStatus === "archived"
                  ? "Archive"
                  : "Publish"}{" "}
              Event
            </h3>

            <p
              style={{
                fontSize: "14px",
                color: "#666666",
                marginBottom: "24px",
                lineHeight: "1.5",
                margin: "0 0 24px 0",
              }}
            >
              Are you sure you want to {eventStatus} "
              {selectedEvent?.event_name}"?
              <br />
              This action cannot be undone.
            </p>

            <div
              style={{ display: "flex", gap: "12px", justifyContent: "center" }}
            >
              <button
                onClick={handleCancelStatusChange}
                style={{
                  padding: "10px 20px",
                  border: "2px solid #e5e7eb",
                  backgroundColor: "#ffffff",
                  color: "#374151",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = "#f9fafb";
                  e.target.style.borderColor = "#d1d5db";
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = "#ffffff";
                  e.target.style.borderColor = "#e5e7eb";
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmStatusChange}
                style={{
                  padding: "10px 20px",
                  border: "none",
                  backgroundColor:
                    eventStatus === "deleted" ? "#ef4444" : "#184778",
                  color: "#ffffff",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: "pointer",
                  transition: "background-color 0.2s ease-in-out",
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor =
                    eventStatus === "deleted" ? "#dc2626" : "#3367d6";
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor =
                    eventStatus === "deleted" ? "#ef4444" : "#184778";
                }}
              >
                {eventStatus === "deleted"
                  ? "Delete"
                  : eventStatus === "archived"
                    ? "Archive"
                    : "Publish"}
              </button>
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderMainContent = () => {
    return (
      <div
        style={{
          minHeight: "100vh",
          backgroundColor: "#f9fafb",
          padding: "24px",
        }}
      >
        <div style={{}}>
          {renderHeader()}
          {renderTabs()}
          {renderEventGrid()}
        </div>
      </div>
    );
  };

  // Main Render Method
  return (
    <>
      <ToastMessage />
      {renderMainContent()}
      {renderConfirmationModal()}

      <AddUpdateEvent
        isEventSidebarOpen={isEventSidebarOpen}
        setEventSidebarOpen={setEventSidebarOpen}
      />

      {/* Overlay for sidebar */}
      {isEventSidebarOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 999,
          }}
          onClick={() => setEventSidebarOpen(false)}
        />
      )}
    </>
  );
};

export default EventManagement;